import React, { useState, useEffect } from 'react';
import { Table } from 'antd';
import { Resizable } from 'react-resizable';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import 'react-resizable/css/styles.css';

const ResizeHandle = React.forwardRef((props, ref) => {
  const { handleAxis, ...restProps } = props;
  return (
    <span
      ref={ref}
      className={`react-resizable-handle react-resizable-handle-${handleAxis}`}
      {...restProps}
      onClick={(e) => {
        e.stopPropagation();
        if (restProps.onClick) restProps.onClick(e);
      }}
      onMouseDown={(e) => {
        e.stopPropagation();
        if (restProps.onMouseDown) restProps.onMouseDown(e);
      }}
      onTouchStart={(e) => {
        e.stopPropagation();
        if (restProps.onTouchStart) restProps.onTouchStart(e);
      }}
      onPointerDown={(e) => {
        e.stopPropagation();
        if (restProps.onPointerDown) restProps.onPointerDown(e);
      }}
      style={{
        position: 'absolute',
        right: 0,
        bottom: 0,
        top: 0,
        width: '10px',
        cursor: 'col-resize',
        zIndex: 1,
      }}
    />
  );
});

// Component for both Sortable and Resizable Header Cell
const ResizableSortableHeaderCell = (props) => {
  const { id, width, onResize, className, ...restProps } = props;

  // Setup Sortable
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: id || 'unknown' });

  const style = {
    ...restProps.style,
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: 'move',
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  if (!id || !width) {
    return (
      <th
        {...restProps}
        className={className}
      />
    );
  }

  // Intercept the resize handle click to prevent drag
  return (
    <Resizable
      width={width}
      height={0}
      handle={<ResizeHandle />}
      onResize={onResize}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th
        {...restProps}
        ref={setNodeRef}
        style={{ ...style, position: 'relative' }}
        {...attributes}
        {...listeners}
        className={`${className || ''} resizable-sortable-th`}
      />
    </Resizable>
  );
};

export default function AdvancedTable({ columns: defaultColumns, ...props }) {
  const [columns, setColumns] = useState(() =>
    defaultColumns.map((col, index) => ({
      ...col,
      key: col.key || col.dataIndex?.toString() || index.toString(),
      width: col.width || 120, // Default width if none provided
    }))
  );

  // Sync state if external columns change
  useEffect(() => {
    setColumns((prev) => {
      const newCols = [];
      const updatedKeys = new Set();
      
      prev.forEach((pCol) => {
        const matchingNewCol = defaultColumns.find(c => 
          (c.key || c.dataIndex?.toString()) === (pCol.key || pCol.dataIndex?.toString())
        );
        if (matchingNewCol) {
          newCols.push({ ...matchingNewCol, key: pCol.key, width: pCol.width });
          updatedKeys.add(pCol.key);
        }
      });
      
      defaultColumns.forEach((c, index) => {
        const key = c.key || c.dataIndex?.toString() || index.toString();
        if (!updatedKeys.has(key)) {
          newCols.push({ ...c, key, width: c.width || 120 });
        }
      });
      
      return newCols;
    });
  }, [defaultColumns]);

  const handleResize = (index) => (e, { size }) => {
    e.stopImmediatePropagation();
    setColumns((prev) => {
      const nextCols = [...prev];
      nextCols[index] = {
        ...nextCols[index],
        width: size.width,
      };
      return nextCols;
    });
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  const onDragEnd = ({ active, over }) => {
    if (active.id !== over?.id) {
      setColumns((prev) => {
        const activeIndex = prev.findIndex((i) => i.key === active.id);
        const overIndex = prev.findIndex((i) => i.key === over?.id);
        return arrayMove(prev, activeIndex, overIndex);
      });
    }
  };

  const mergedColumns = columns.map((col, index) => ({
    ...col,
    onHeaderCell: (column) => ({
      width: column.width,
      onResize: handleResize(index),
      id: column.key,
    }),
  }));

  const itemIds = columns.map((col) => col.key);

  return (
    <DndContext
      sensors={sensors}
      modifiers={[restrictToHorizontalAxis]}
      onDragEnd={onDragEnd}
      collisionDetection={closestCenter}
    >
      <SortableContext items={itemIds} strategy={horizontalListSortingStrategy}>
        <Table
          {...props}
          columns={mergedColumns}
          components={{
            header: {
              cell: ResizableSortableHeaderCell,
            },
          }}
        />
      </SortableContext>
    </DndContext>
  );
}
